import { reactive } from 'vue'

export interface UsePendingAction {
  /** Whether `key` currently has an action in flight. */
  isPending: (key: string) => boolean
  /** Marks `key` pending. Returns `false` (and does nothing) if it already was. */
  start: (key: string) => boolean
  /** Clears `key`'s pending flag. Safe to call when it was never set. */
  finish: (key: string) => void
  /** Runs `fn` under `key`'s pending flag, clearing it when `fn` settles; a call for a key already in flight is ignored and resolves to `undefined`. */
  run: <T>(key: string, fn: () => Promise<T>) => Promise<T | undefined>
}

/** Owns one component's in-flight flags, one per caller-chosen action key (`${action}:${id}` for rows). */
export function usePendingAction(): UsePendingAction {
  const keys = reactive(new Set<string>())

  function isPending(key: string): boolean {
    return keys.has(key)
  }

  function start(key: string): boolean {
    if (keys.has(key)) return false
    keys.add(key)
    return true
  }

  function finish(key: string): void {
    keys.delete(key)
  }

  async function run<T>(key: string, fn: () => Promise<T>): Promise<T | undefined> {
    if (!start(key)) return undefined
    try {
      return await fn()
    } finally {
      finish(key)
    }
  }

  return { isPending, start, finish, run }
}
